/**
 * Unified Analytics Module
 *
 * Sends events to Google Analytics, GTM dataLayer, PostHog, and Meta Pixel.
 * Usage: import { analytics } from "@/lib/analytics"
 */

import { getPostHogDistinctId, trackPostHogEvent } from "@/components/posthog-provider";
import {
  getAnalyticsBrandProperties,
  resolveAnalyticsBrandConfig,
  writeBrandUtmStorage,
  type AnalyticsBrandConfig,
} from "@/lib/analytics-brand-config";
import type { LeadAttribution } from "@/lib/lead-attribution";
import { sendRomaReservationClickGoogleAdsConversion } from "@/lib/google-ads-conversions";
import { getMetaCapiEventName } from "@/lib/meta-events";
import { siteConfig } from "@/lib/site-config";

type EventProperties = Record<string, string | number | boolean | undefined>;

type CtaEventProperties = {
  location?: string;
  label?: string;
  source?: string;
  cta_text?: string;
  destination_url?: string;
  page_path?: string;
  platform?: string;
  link_type?: string;
  order_target?: string;
  lead_type?: string;
  exit_target?: string;
  exit_type?: string;
  reservation_provider?: string;
  is_external?: boolean;
};
type AnalyticsContext = Record<string, string | number | undefined>;

const LEGACY_UTM_STORAGE_KEYS = ["tuscan_cove_utm_params", "roma_utm_params"];
const LEGACY_ATTRIBUTION_STORAGE_KEY = "tuscan_cove_latest_attribution";
const LEGACY_FIRST_TOUCH_STORAGE_KEY = "tuscan_cove_first_touch_attribution";
const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
] as const;
const ATTRIBUTION_KEY_MAP: Record<(typeof ATTRIBUTION_KEYS)[number], keyof LeadAttribution> = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  fbclid: "fbclid",
  msclkid: "msclkid",
  ttclid: "ttclid",
  twclid: "twclid",
  li_fat_id: "liFatId",
};
const PII_KEY_PATTERN = /(^|_)(email|e_mail|phone|tel|mobile|first_name|firstName|last_name|lastName|full_name|fullName|name|address|street|message|comment|notes?|free_text|form_text)($|_)/i;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_PATTERN = /(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/;

// Standard Meta Pixel events that should use 'track' instead of 'trackCustom'
const STANDARD_META_EVENTS = new Set([
  "PageView",
  "ViewContent",
  "Search",
  "AddToCart",
  "InitiateCheckout",
  "AddPaymentInfo",
  "Purchase",
  "Lead",
  "CompleteRegistration",
  "Schedule",
  "Contact",
  "FindLocation",
  "Subscribe",
  "StartTrial",
]);

function safeString(value: string, maxLength = 160) {
  const trimmed = value.trim();
  if (!trimmed || EMAIL_PATTERN.test(trimmed) || PHONE_PATTERN.test(trimmed)) {
    return undefined;
  }

  return trimmed.slice(0, maxLength);
}

function sanitizeUrl(url: string) {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return safeString(url);
  }
}

function getBrandConfig(): AnalyticsBrandConfig | null {
  if (typeof window === "undefined") return null;
  return resolveAnalyticsBrandConfig();
}

function getOrCreateAnalyticsSessionId(brandConfig: AnalyticsBrandConfig) {
  if (typeof window === "undefined") return undefined;

  try {
    const existing = window.sessionStorage.getItem(brandConfig.storage.sessionId);
    if (existing) return existing;

    const prefix = brandConfig.brandId === "roma-kitchen" ? "rks" : "tcs";
    const nextId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(brandConfig.storage.sessionId, nextId);
    return nextId;
  } catch {
    const prefix = brandConfig.brandId === "roma-kitchen" ? "rks" : "tcs";
    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
}

function safeParseStorage(storage: Storage, key: string) {
  const value = storage.getItem(key);
  if (!value) return undefined;

  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function getUrlAttributionParams() {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key);
    const safeValue = value ? safeString(value, 220) : undefined;
    if (safeValue) attribution[key] = safeValue;
  });

  return omitUndefined(attribution);
}

function getLatestAttributionParams() {
  if (typeof window === "undefined") return {};

  const brandConfig = getBrandConfig();
  const urlAttribution = getUrlAttributionParams();
  const hasUrlAttribution = Object.keys(urlAttribution).length > 0;

  try {
    if (hasUrlAttribution) {
      if (brandConfig) {
        window.sessionStorage.setItem(
          brandConfig.storage.latestAttribution,
          JSON.stringify(urlAttribution),
        );
        writeBrandUtmStorage(brandConfig, urlAttribution);
      } else {
        window.sessionStorage.setItem(
          LEGACY_ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(urlAttribution),
        );
        window.sessionStorage.setItem(
          LEGACY_UTM_STORAGE_KEYS[0],
          JSON.stringify(urlAttribution),
        );
      }
      return urlAttribution;
    }

    const stored =
      (brandConfig
        ? safeParseStorage(window.sessionStorage, brandConfig.storage.latestAttribution) ??
          safeParseStorage(window.sessionStorage, brandConfig.storage.utmParams)
        : undefined) ??
      safeParseStorage(window.sessionStorage, LEGACY_ATTRIBUTION_STORAGE_KEY) ??
      safeParseStorage(window.sessionStorage, LEGACY_UTM_STORAGE_KEYS[0]) ??
      safeParseStorage(window.sessionStorage, LEGACY_UTM_STORAGE_KEYS[1]) ??
      {};

    return Object.entries(stored).reduce<Record<string, string>>((params, [key, value]) => {
      if (!ATTRIBUTION_KEYS.includes(key as (typeof ATTRIBUTION_KEYS)[number])) return params;
      const safeValue = typeof value === "string" ? safeString(value, 220) : undefined;
      if (safeValue) params[key] = safeValue;
      return params;
    }, {});
  } catch {
    return urlAttribution;
  }
}

function getReferrerHost(referrer: string | undefined) {
  if (!referrer || referrer === "direct") return undefined;

  try {
    return new URL(referrer).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function inferTrafficSource(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_source) return params.utm_source;
  if (params.gclid || params.gbraid || params.wbraid) return "google";
  if (params.fbclid) return "facebook";
  if (params.msclkid) return "bing";
  if (params.ttclid) return "tiktok";
  if (params.twclid) return "x";
  if (params.li_fat_id) return "linkedin";
  if (!referrerHost) return "direct";

  if (/google\./i.test(referrerHost)) return "google";
  if (/bing\./i.test(referrerHost)) return "bing";
  if (/yahoo\./i.test(referrerHost)) return "yahoo";
  if (/duckduckgo\./i.test(referrerHost)) return "duckduckgo";
  if (/facebook\.|instagram\.|l\.facebook/i.test(referrerHost)) return "meta";
  if (/tiktok\./i.test(referrerHost)) return "tiktok";
  if (/linkedin\./i.test(referrerHost)) return "linkedin";

  return referrerHost;
}

function inferTrafficMedium(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_medium) return params.utm_medium;
  if (params.gclid || params.gbraid || params.wbraid || params.fbclid || params.msclkid) {
    return "paid";
  }
  if (!referrerHost) return "direct";
  if (/google\.|bing\.|yahoo\.|duckduckgo\./i.test(referrerHost)) return "organic";
  if (/facebook\.|instagram\.|l\.facebook|tiktok\.|linkedin\./i.test(referrerHost)) {
    return "social_referral";
  }
  return "referral";
}

function getFirstTouch(
  params: Record<string, string>,
  referrer: string,
  brandConfig: AnalyticsBrandConfig | null,
) {
  if (typeof window === "undefined") return {};

  const storageKey =
    brandConfig?.storage.firstTouchAttribution ?? LEGACY_FIRST_TOUCH_STORAGE_KEY;

  try {
    const stored = safeParseStorage(window.localStorage, storageKey);
    if (stored && Object.keys(stored).length) return stored;

    const firstTouch = {
      landingPage: window.location.pathname,
      firstReferrer: referrer,
      createdAt: Date.now(),
      ...params,
    };
    window.localStorage.setItem(storageKey, JSON.stringify(firstTouch));
    return firstTouch;
  } catch {
    return {
      landingPage: window.location.pathname,
      firstReferrer: referrer,
      ...params,
    };
  }
}

export function getLeadAttribution(formSource?: string): LeadAttribution | undefined {
  if (typeof window === "undefined") return undefined;

  const brandConfig = getBrandConfig();
  const params = getLatestAttributionParams();
  const referrer = document.referrer ? (sanitizeUrl(document.referrer) ?? "direct") : "direct";
  const referrerHost = getReferrerHost(referrer);
  const firstTouch = getFirstTouch(params, referrer, brandConfig);
  const attribution: LeadAttribution = {
    sourcePage: window.location.pathname,
    pageLocation: sanitizeUrl(window.location.href),
    landingPage:
      typeof firstTouch.landingPage === "string" ? firstTouch.landingPage : window.location.pathname,
    firstReferrer:
      typeof firstTouch.firstReferrer === "string" ? firstTouch.firstReferrer : referrer,
    referrer,
    referrerHost,
    trafficSource: inferTrafficSource(params, referrerHost),
    trafficMedium: inferTrafficMedium(params, referrerHost),
    deviceCategory: getDeviceCategory(),
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    timezone: safeString(Intl.DateTimeFormat().resolvedOptions().timeZone),
    posthogDistinctId: getPostHogDistinctId(),
    analyticsSessionId: brandConfig
      ? getOrCreateAnalyticsSessionId(brandConfig)
      : undefined,
    formSource: safeString(formSource ?? ""),
    submittedAt: Date.now(),
  };

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = params[key];
    if (!value) return;
    const targetKey = ATTRIBUTION_KEY_MAP[key];
    Object.assign(attribution, { [targetKey]: value });
  });

  return attribution;
}

function sanitizeDestinationUrl(destinationUrl: string) {
  if (destinationUrl.startsWith("tel:")) {
    const destinationDigits = destinationUrl.replace(/\D/g, "");
    const restaurantDigits = siteConfig.contact.phone.replace(/\D/g, "");
    return destinationDigits === restaurantDigits
      ? `tel:${siteConfig.contact.phone}`
      : undefined;
  }

  if (destinationUrl.startsWith("http") || destinationUrl.startsWith("/")) {
    return sanitizeUrl(destinationUrl);
  }

  return safeString(destinationUrl);
}

function getStoredAttribution() {
  if (typeof window === "undefined") return {};

  const attribution: AnalyticsContext = {};

  try {
    const latestAttribution = getLatestAttributionParams();
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = latestAttribution[key];
      if (value) attribution[key] = value;
    });

    const brandConfig = getBrandConfig();
    const storageKeys = brandConfig
      ? [brandConfig.storage.utmParams, ...LEGACY_UTM_STORAGE_KEYS]
      : LEGACY_UTM_STORAGE_KEYS;

    storageKeys.forEach((storageKey) => {
      const storedValue = window.sessionStorage.getItem(storageKey);
      if (!storedValue) return;

      try {
        const parsedValue = JSON.parse(storedValue) as Record<string, unknown>;
        ATTRIBUTION_KEYS.forEach((key) => {
          if (attribution[key] !== undefined) return;

          const value = parsedValue[key];
          if (typeof value === "string") {
            attribution[key] = safeString(value);
          }
        });
      } catch {
        // Ignore malformed legacy attribution payloads.
      }
    });

    ATTRIBUTION_KEYS.forEach((key) => {
      if (attribution[key] !== undefined) return;

      const value = window.sessionStorage.getItem(key);
      if (value) {
        attribution[key] = safeString(value);
      }
    });
  } catch {
    // sessionStorage can be blocked or contain legacy malformed JSON.
  }

  return attribution;
}

function getDeviceCategory() {
  if (typeof window === "undefined") return undefined;

  const userAgent = window.navigator.userAgent;
  const width = window.innerWidth;
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent) || (width >= 768 && width < 1024)) {
    return "tablet";
  }

  if (/Mobi|iPhone|iPod|Android/i.test(userAgent) || width < 768) {
    return "mobile";
  }

  return "desktop";
}

function omitUndefined<T extends object>(value: T) {
  return Object.entries(value).reduce<Partial<T>>((cleanValue, [key, entryValue]) => {
    if (entryValue !== undefined) {
      cleanValue[key as keyof T] = entryValue as T[keyof T];
    }
    return cleanValue;
  }, {}) as T;
}

function createAnalyticsEventId(eventName: string) {
  const prefix = eventName.replace(/[^a-z0-9_]/gi, "_").slice(0, 40) || "event";
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function getAnalyticsContext(): AnalyticsContext {
  if (typeof window === "undefined") return {};

  const brandConfig = getBrandConfig();

  return {
    page_path: window.location.pathname,
    page_location: sanitizeUrl(window.location.href),
    page_title: safeString(document.title),
    referrer: document.referrer ? sanitizeUrl(document.referrer) : "direct",
    device_category: getDeviceCategory(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    timezone: safeString(Intl.DateTimeFormat().resolvedOptions().timeZone),
    ...(brandConfig ? getAnalyticsBrandProperties(brandConfig) : {}),
    ...getStoredAttribution(),
  };
}

function sanitizeEventProperties(properties?: EventProperties): EventProperties {
  if (!properties) return {};

  return Object.entries(properties).reduce<EventProperties>((safeProperties, [key, value]) => {
    if (PII_KEY_PATTERN.test(key) || value === undefined) return safeProperties;

    if (typeof value === "string") {
      if (key === "destination_url") {
        const safeDestinationUrl = sanitizeDestinationUrl(value);
        if (safeDestinationUrl !== undefined) {
          safeProperties[key] = safeDestinationUrl;
        }
        return safeProperties;
      }

      const safeValue = safeString(value);
      if (safeValue !== undefined) {
        safeProperties[key] = safeValue;
      }
      return safeProperties;
    }

    safeProperties[key] = value;
    return safeProperties;
  }, {});
}

export function getAnalyticsEventProperties(properties?: EventProperties): EventProperties {
  return {
    ...getAnalyticsContext(),
    ...sanitizeEventProperties(properties),
  };
}

// Unified dataLayer push for GTM
function pushToDataLayer(event: string, data?: Record<string, unknown>) {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ...data,
      timestamp: new Date().toISOString(),
    });
  }
}

function trackGoogleAnalytics(eventName: string, properties?: EventProperties) {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", eventName, {
    event_category: properties?.category || "engagement",
    event_label: properties?.label,
    value: properties?.value,
    ...properties,
  });
}

export function getMetaPixelEventName(eventName: string) {
  if (eventName === "page_view") return "PageView";
  if (eventName === "lead_captured") return "Lead";
  if (eventName === "reservation_completed") return "Schedule";
  if (eventName === "checkout_started") return "InitiateCheckout";
  if (eventName === "order_completed") return "Purchase";
  if (eventName === "phone_clicked") return "Contact";
  if (eventName === "directions_clicked" || eventName === "map_clicked") return "FindLocation";
  if (eventName === "external_order_exit_completed") return "InitiateCheckout";

  return eventName;
}

// Send event to Meta Pixel using standard events whenever a conversion maps cleanly.
function trackMetaPixel(eventName: string, properties?: EventProperties, eventId?: string) {
  if (typeof window === "undefined" || !window.fbq) return;

  try {
    const metaEventName = getMetaPixelEventName(eventName);
    // Prepare Meta Pixel parameters
    const metaParams: Record<string, unknown> = {};
    if (properties?.category) metaParams.content_category = properties.category;
    if (properties?.label) metaParams.content_name = properties.label;
    
    // Add currency when value is present (required for Meta value optimization)
    if (properties?.value) {
      metaParams.value = properties.value;
      metaParams.currency = "USD";
    }
    
    // Add content_ids for product-related events
    if (properties?.content_id) {
      metaParams.content_ids = [properties.content_id];
      metaParams.content_type = "product";
    }
    
    // Add any additional properties
    Object.keys(properties || {}).forEach((key) => {
      if (key !== "category" && key !== "label" && key !== "value" && key !== "content_id") {
        metaParams[key] = properties![key];
      }
    });

    // Use trackCustom for custom events, track for standard events
    const isStandardEvent = STANDARD_META_EVENTS.has(metaEventName);
    if (isStandardEvent) {
      window.fbq("track", metaEventName, metaParams, eventId ? { eventID: eventId } : undefined);
    } else {
      window.fbq("trackCustom", metaEventName, metaParams, eventId ? { eventID: eventId } : undefined);
    }
  } catch (error) {
    // Silently fail if Meta Pixel isn't available
    console.debug("Meta Pixel tracking error:", error);
  }
}

function getCookieValue(name: string) {
  if (typeof document === "undefined") return undefined;

  return document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function getMetaCapiCustomData(properties: EventProperties) {
  const customData: Record<string, string | number | boolean> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined) return;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      customData[key] = value;
    }
  });

  if (typeof properties.value === "number") {
    customData.value = properties.value;
    customData.currency = typeof properties.currency === "string" ? properties.currency : "USD";
  }

  return customData;
}

function trackMetaCapi(eventName: string, properties: EventProperties, eventId: string) {
  if (typeof window === "undefined" || !getMetaCapiEventName(eventName)) return;

  const brandConfig = getBrandConfig();
  const payload = {
    eventName,
    eventId,
    eventSourceUrl: window.location.href,
    eventTime: Math.floor(Date.now() / 1000),
    pixelId: brandConfig?.metaPixelId || undefined,
    userData: {
      fbp: getCookieValue("_fbp"),
      fbc: getCookieValue("_fbc"),
    },
    customData: getMetaCapiCustomData(properties),
  };

  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon("/api/meta/capi", blob);
      return;
    }

    void fetch("/api/meta/capi", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    });
  } catch (error) {
    console.debug("Meta CAPI tracking error:", error);
  }
}

// Core tracking function that sends to all platforms
function track(eventName: string, properties?: EventProperties) {
  const enrichedProperties = getAnalyticsEventProperties(properties);
  const eventId = createAnalyticsEventId(eventName);

  // Send to PostHog
  trackPostHogEvent(eventName, enrichedProperties);

  // Send directly to GA4. Production does not require GTM for core restaurant events.
  // EXCEPTION: GA4's page_view is owned solely by GoogleAnalyticsPageView
  // (google-analytics.tsx), which sends it with proper page_location,
  // page_referrer, and attribution properties on every route change. Sending
  // it from here too double-counted every SPA navigation in GA4.
  if (eventName !== "page_view") {
    trackGoogleAnalytics(eventName, enrichedProperties);
  }

  // Also push to GTM dataLayer when a GTM container is configured.
  pushToDataLayer(eventName, {
    event_category: enrichedProperties.category || "engagement",
    event_label: enrichedProperties.label,
    event_value: enrichedProperties.value,
    ...enrichedProperties,
  });

  // Send to Meta Pixel using trackCustom for custom events
  trackMetaPixel(eventName, enrichedProperties, eventId);
  trackMetaCapi(eventName, enrichedProperties, eventId);
}

function getCurrentPagePath() {
  if (typeof window === "undefined") return undefined;
  return window.location.pathname;
}

function withPageContext(properties: EventProperties = {}): EventProperties {
  return {
    page_path: getCurrentPagePath(),
    ...properties,
  };
}

function trackExternalExit(properties: EventProperties = {}) {
  track("external_exit_clicked", withPageContext({
    ...properties,
    category: "external_exit",
    label: properties.exit_target ?? properties.link_type ?? properties.platform ?? properties.label,
  }));
}

export function inferReservationClickContext(
  source: string,
  properties?: CtaEventProperties
): {
  reservation_provider: "google_maps" | "internal" | "yelp";
  is_external: boolean;
  destination_url?: string;
} {
  const destinationUrl =
    properties?.destination_url ??
    (source === "yelp_direct_fallback" ? siteConfig.yelpReservationWidgetUrl : undefined);

  const isGoogleReserveExit =
    properties?.reservation_provider === "google_maps" ||
    properties?.platform === "google_maps" ||
    (typeof destinationUrl === "string" &&
      /(?:google\.[^/]+\/maps\/reserve|maps\.google\.)/i.test(destinationUrl));

  if (isGoogleReserveExit) {
    return {
      reservation_provider: "google_maps",
      is_external: true,
      destination_url: destinationUrl,
    };
  }

  const isYelpExit =
    source === "yelp_direct_fallback" ||
    properties?.reservation_provider === "yelp" ||
    (typeof destinationUrl === "string" && /yelp\.com/i.test(destinationUrl));

  if (isYelpExit) {
    return {
      reservation_provider: "yelp",
      is_external: true,
      destination_url: destinationUrl,
    };
  }

  const isExternal =
    properties?.is_external ??
    (typeof destinationUrl === "string" && /^https?:\/\//i.test(destinationUrl));

  return {
    reservation_provider: "internal",
    is_external: isExternal,
    destination_url: destinationUrl,
  };
}

export function getPartySizeBucket(partySize: number | string | undefined) {
  const normalizedPartySize =
    typeof partySize === "string" ? Number.parseInt(partySize, 10) : partySize;

  if (typeof normalizedPartySize !== "number" || !Number.isFinite(normalizedPartySize)) {
    return "unknown";
  }
  if (normalizedPartySize < 1) return "unknown";
  if (normalizedPartySize <= 2) return "1_2";
  if (normalizedPartySize <= 4) return "3_4";
  if (normalizedPartySize <= 8) return "5_8";
  if (normalizedPartySize <= 12) return "9_12";
  if (normalizedPartySize <= 24) return "13_24";
  if (normalizedPartySize <= 50) return "25_50";

  return "51_plus";
}

type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function isFormControl(target: EventTarget): target is FormControl {
  if (
    typeof HTMLInputElement === "undefined" ||
    typeof HTMLSelectElement === "undefined" ||
    typeof HTMLTextAreaElement === "undefined"
  ) {
    return false;
  }

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLSelectElement ||
    target instanceof HTMLTextAreaElement
  );
}

export function getFormControlFieldKey(target: EventTarget) {
  if (!isFormControl(target)) return "unknown";

  return target.name || target.id || "unknown";
}

export function getFormControlValidityErrorType(target: EventTarget) {
  if (!isFormControl(target)) return "invalid_value";

  const { validity } = target;
  if (validity.valueMissing) return "missing_required";
  if (validity.typeMismatch || validity.patternMismatch) return "invalid_format";
  if (validity.tooShort) return "too_short";
  if (validity.tooLong) return "too_long";
  if (validity.rangeUnderflow) return "below_minimum";
  if (validity.rangeOverflow) return "above_maximum";
  if (validity.stepMismatch) return "invalid_step";
  if (validity.badInput) return "bad_input";

  return "invalid_value";
}

// Pre-defined events for consistency
export const analytics = {
  // Core tracking
  track,
  bootstrapAttribution: () => {
    getLatestAttributionParams();
  },

  // Email Popup Events
  popupViewed: () => track("popup_viewed", { category: "email_capture" }),
  popupClosed: () => track("popup_closed", { category: "email_capture" }),
  incentivePopupViewed: (properties: EventProperties) => track("incentive_popup_viewed", {
    category: "incentive_popup",
    ...properties,
  }),
  incentivePopupStep1Yes: (properties: EventProperties) => track("incentive_popup_step1_yes", {
    category: "incentive_popup",
    ...properties,
  }),
  incentivePopupDismissed: (properties: EventProperties) => track("incentive_popup_dismissed", {
    category: "incentive_popup",
    ...properties,
  }),
  incentivePopupSubmitted: (properties: EventProperties) => track("incentive_popup_submitted", {
    category: "incentive_popup",
    ...properties,
  }),
  incentivePopupBirthdaySaved: (properties: EventProperties) => track("incentive_popup_birthday_saved", {
    category: "incentive_popup",
    ...properties,
  }),
  formStarted: (properties?: EventProperties) => track("form_started", {
    category: properties?.category ?? "email_capture",
    ...properties,
  }),
  formAttempted: (properties?: EventProperties) => track("form_attempted", withPageContext({
    category: "form",
    ...properties,
  })),
  formValidationFailed: (properties?: EventProperties) => track("form_validation_failed", withPageContext({
    category: "form",
    ...properties,
  })),
  formSubmitFailed: (properties?: EventProperties) => track("form_submit_failed", withPageContext({
    category: "form",
    ...properties,
  })),
  formSubmitSucceeded: (properties?: EventProperties) => track("form_submit_succeeded", withPageContext({
    category: "form",
    ...properties,
  })),
  formSubmitted: (source: string) => track("form_submitted", {
    category: "email_capture",
    label: source
  }),
  subscribeSuccess: (isNew: boolean, properties?: EventProperties) => track("subscribe_success", {
    category: "email_capture",
    label: isNew ? "new_subscriber" : "returning_subscriber",
    ...properties,
  }),
  // Distinct money-page lead conversions. Fire IN ADDITION to the generic
  // inquiry_submitted/lead_captured events so GA4 + PostHog get a clean per-form
  // conversion event name without property filtering. Intentionally NOT mapped
  // in meta-events.ts, so Meta CAPI ignores them (no double-count of the
  // existing lead_captured -> Lead). Not brand-gated: fires for every tenant.
  cateringInquirySubmitted: (properties?: EventProperties) => track("catering_inquiry_submitted", withPageContext({
    category: "conversion",
    label: "catering",
    lead_type: "catering",
    inquiry_type: "catering",
    source: "catering_form",
    ...properties,
  })),
  privateEventInquirySubmitted: (properties?: EventProperties) => track("private_event_inquiry_submitted", withPageContext({
    category: "conversion",
    label: "private_event",
    lead_type: "private_event",
    inquiry_type: "private_event",
    source: "private_events_form",
    ...properties,
  })),
  leadCaptured: (leadType: string, source: string, properties?: EventProperties) => track("lead_captured", withPageContext({
    category: "conversion",
    label: `${leadType}_${source}`,
    lead_type: leadType,
    source,
    ...properties,
  })),

  // Post-Signup Actions
  reservationClicked: (source: string, properties?: CtaEventProperties) => {
    const reservationContext = inferReservationClickContext(source, properties);
    const eventProperties = withPageContext({
      category: "conversion",
      label: source,
      source,
      location: properties?.location ?? source,
      lead_type: properties?.lead_type ?? "reservation",
      ...properties,
      ...reservationContext,
    });

    track("reservation_clicked", eventProperties);

    if (reservationContext.is_external) {
      sendRomaReservationClickGoogleAdsConversion();
      trackExternalExit({
        ...eventProperties,
        category: "external_exit",
        exit_type: "reservation",
        exit_target: reservationContext.reservation_provider,
        provider: reservationContext.reservation_provider,
      });
    }
  },
  menuBrowseClicked: (properties?: CtaEventProperties) => track("menu_browse_clicked", withPageContext({
    category: "engagement",
    label: properties?.location,
    ...properties,
  })),

  // Navigation Events
  pageView: (pageName: string) => track("page_view", {
    category: "navigation",
    label: pageName
  }),
  ctaClicked: (ctaName: string, location: string, properties?: CtaEventProperties) => track("cta_clicked", withPageContext({
    category: "engagement",
    label: `${ctaName}_${location}`,
    cta_text: ctaName,
    location,
    ...properties,
  })),

  // Menu Events
  menuItemViewed: (itemName: string, price: number, itemId?: string) => track("ViewContent", {
    category: "menu",
    label: itemName,
    value: price,
    content_id: itemId,
  }),
  menuCategoryViewed: (category: string) => track("menu_category_viewed", {
    category: "menu",
    label: category
  }),
  menuDepthItemViewed: (properties: EventProperties) => track("menu_item_viewed", {
    category: "menu",
    ...properties,
  }),
  sectionViewed: (section: string, properties?: EventProperties) => track("section_viewed", withPageContext({
    category: "engagement",
    label: section,
    section,
    ...properties,
  })),

  // Reservation Events
  reservationStarted: () => track("reservation_started", { category: "conversion" }),
  reservationCompleted: (properties?: EventProperties) => track("reservation_completed", {
    category: "conversion",
    ...properties,
  }),
  formStepViewed: (formType: string, step: string, stepNumber?: number) => track("form_step_viewed", {
    category: "form",
    label: `${formType}_${step}`,
    form_type: formType,
    step,
    step_number: stepNumber,
  }),
  dateSelected: (formType: string, properties?: EventProperties) => track("date_selected", {
    category: "form",
    form_type: formType,
    ...properties,
  }),
  partySizeSelected: (formType: string, partySize: number, properties?: EventProperties) => track("party_size_selected", {
    category: "form",
    form_type: formType,
    party_size: partySize,
    ...properties,
  }),

  // Contact Events
  inquiryStarted: (type: string, source: string) => track("inquiry_started", {
    category: "conversion",
    label: `${type}_${source}`,
    inquiry_type: type,
    source,
  }),
  inquirySubmitted: (type: string, source?: string, properties?: EventProperties) => track("inquiry_submitted", {
    category: "conversion",
    label: source ? `${type}_${source}` : type,
    inquiry_type: type,
    source,
    ...properties,
  }),
  contactFormSubmitted: (type: string, properties?: EventProperties) => track("contact_form_submitted", {
    category: "conversion",
    label: type,
    ...properties,
  }),
  phoneClicked: (properties?: CtaEventProperties) => {
    const eventProperties = withPageContext({
      category: "contact",
      label: properties?.location,
      link_type: "phone",
      exit_target: "phone",
      exit_type: "contact",
      ...properties,
    });

    track("phone_clicked", eventProperties);
    trackExternalExit(eventProperties);
  },
  mapClicked: (properties?: CtaEventProperties) => {
    const eventProperties = withPageContext({
      category: "contact",
      label: properties?.location,
      link_type: "map",
      exit_target: "maps",
      exit_type: "directions",
      ...properties,
    });

    track("map_clicked", eventProperties);
    trackExternalExit(eventProperties);
  },
  directionsClicked: (properties?: CtaEventProperties) => {
    const eventProperties = withPageContext({
      category: "contact",
      label: properties?.location,
      link_type: "directions",
      exit_target: "maps",
      exit_type: "directions",
      ...properties,
    });

    track("directions_clicked", eventProperties);
    trackExternalExit(eventProperties);
  },
  externalExitClicked: (properties?: EventProperties) => trackExternalExit(properties),

  // Social Events
  socialClicked: (platform: string, properties?: CtaEventProperties) => {
    const eventProperties = withPageContext({
      category: "social",
      label: platform,
      platform,
      exit_target: platform,
      exit_type: "social",
      ...properties,
    });

    track("social_clicked", eventProperties);
    trackExternalExit(eventProperties);
  },

  // Order Events - Full Funnel
  orderStarted: (properties?: CtaEventProperties) => {
    const eventProperties = withPageContext({
      category: "conversion",
      label: properties?.location,
      exit_target: properties?.order_target,
      exit_type: properties?.is_external ? "ordering" : undefined,
      ...properties,
    });

    track("order_started", eventProperties);

    if (properties?.is_external || properties?.order_target) {
      trackExternalExit({
        ...eventProperties,
        category: "external_exit",
        exit_target: properties?.order_target ?? "ordering",
        exit_type: "ordering",
      });

      if (properties?.is_external) {
        track("external_order_exit_completed", withPageContext({
          category: "conversion",
          label: properties?.order_target ?? properties?.platform ?? "external_ordering",
          exit_target: properties?.order_target ?? "external_ordering",
          exit_type: "ordering",
          platform: properties?.platform,
          source: properties?.source,
          order_target: properties?.order_target,
        }));
      }
    }
  },
  cartItemAdded: (itemName: string, price: number, quantity: number) => track("cart_item_added", {
    category: "ecommerce",
    label: itemName,
    value: price,
    quantity,
  }),
  cartItemRemoved: (itemName: string) => track("cart_item_removed", {
    category: "ecommerce",
    label: itemName,
  }),
  cartViewed: (itemCount: number, total: number) => track("cart_viewed", {
    category: "ecommerce",
    value: total,
    item_count: itemCount,
  }),
  checkoutStarted: (total: number, itemCount: number, properties?: EventProperties) => track("checkout_started", {
    category: "conversion",
    value: total,
    item_count: itemCount,
    ...properties,
  }),
  checkoutContactEntered: () => track("checkout_contact_entered", {
    category: "conversion",
  }),
  checkoutPaymentStarted: (total: number) => track("checkout_payment_started", {
    category: "conversion",
    value: total,
  }),
  orderCompleted: (
    orderId: string,
    total: number,
    orderType: string,
    options?: {
      paymentMethod?: "card_online" | "in_person";
      paymentIntentId?: string;
    }
  ) =>
    track("order_completed", {
      category: "conversion",
      label: orderId,
      value: total,
      order_type: orderType,
      payment_method: options?.paymentMethod,
      payment_intent_id: options?.paymentIntentId,
      order_success_type:
        options?.paymentMethod === "in_person"
          ? "first_party_pickup_pay_in_person"
          : options?.paymentMethod === "card_online"
            ? "first_party_online_paid"
            : undefined,
      currency: "USD",
    }),
  orderFailed: (error: string) => track("order_failed", {
    category: "conversion",
    label: error,
  }),
  tipSelected: (tipPercent: number, tipAmount: number) => track("tip_selected", {
    category: "ecommerce",
    value: tipAmount,
    tip_percent: tipPercent,
  }),

  // Engagement Events
  scrollDepth: (depth: number, pagePath: string) => track("scroll_depth", {
    category: "engagement",
    label: pagePath,
    value: depth
  }),
  timeOnPage: (seconds: number, pagePath: string) => track("time_on_page", {
    category: "engagement",
    label: pagePath,
    value: seconds
  }),
  eventRsvpStarted: (eventName: string, properties?: EventProperties) => track("event_rsvp_started", {
    category: "conversion",
    label: eventName,
    ...properties,
  }),
  eventRsvpCompleted: (eventName: string, properties?: EventProperties) => track("event_rsvp_completed", {
    category: "conversion",
    label: eventName,
    ...properties,
  }),
  eventFilterChanged: (filterType: string) => track("event_filter_changed", {
    category: "events",
    label: filterType,
    filter_type: filterType,
  }),
};

// Export dataLayer helper for direct GTM access
export { pushToDataLayer };

// Scroll depth tracking utility
let maxScrollDepth = 0;
const scrollDepthMilestones = [25, 50, 75, 90, 100];
let trackedMilestones: Set<number> = new Set();

export function initScrollTracking(pagePath: string) {
  if (typeof window === "undefined") return;

  maxScrollDepth = 0;
  trackedMilestones = new Set();

  const handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;

    const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
    maxScrollDepth = Math.max(maxScrollDepth, scrollPercent);

    // Track milestones
    scrollDepthMilestones.forEach((milestone) => {
      if (maxScrollDepth >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);
        analytics.scrollDepth(milestone, pagePath);
      }
    });
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
}

// Time on page tracking utility
export function initTimeTracking(pagePath: string) {
  if (typeof window === "undefined") return;

  const startTime = Date.now();
  const milestones = [30, 60, 120, 300]; // seconds
  const trackedTimeMilestones: Set<number> = new Set();

  const interval = setInterval(() => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);

    milestones.forEach((milestone) => {
      if (elapsed >= milestone && !trackedTimeMilestones.has(milestone)) {
        trackedTimeMilestones.add(milestone);
        analytics.timeOnPage(milestone, pagePath);
      }
    });
  }, 5000); // Check every 5 seconds

  return () => {
    clearInterval(interval);
  };
}

// Combined hook for easy integration in pages
export function initPageEngagementTracking(pagePath: string) {
  const cleanupScroll = initScrollTracking(pagePath);
  const cleanupTime = initTimeTracking(pagePath);

  return () => {
    cleanupScroll?.();
    cleanupTime?.();
  };
}
